import {
    UseMutationOptions,
    UseMutationResult,
    useIsMutating,
    useMutation,
    useQuery,
    useQueryClient,
} from '@eika-infrastruktur/react-query';

/**
 * A shareable mutation is a mutation that shares its state with outher components
 * This is currently not possible with react-query
 * for reference, please take a look : https://github.com/TanStack/query/issues/2304
 */

type ShareableMutationOptions<TData = unknown, TError = unknown, TVariables = unknown, TContext = unknown> = {
    mutationFn: (variables: TVariables) => Promise<TData>;
} & Omit<UseMutationOptions<TData, TError, TVariables, TContext>, 'mutationFn'>;

// eslint-disable-next-line import/prefer-default-export
export const useShareableMutation = <TData = unknown, TError = unknown, TVariables = unknown, TContext = unknown>({
    mutationKey,
    mutationFn,
    ...options
}: ShareableMutationOptions<TData, TError, TVariables, TContext>): UseMutationResult<
    TData,
    TError,
    TVariables,
    TContext
> => {
    const queryClient = useQueryClient();
    const query = useQuery<TData, TError>({
        queryKey: ['CustomMutation', mutationKey],
        queryFn: async () => Promise.resolve(false as unknown as TData),
        retry: false,
        gcTime: Infinity,
        staleTime: Infinity,
    });
    const queryError = useQuery<TError, TData>({
        queryKey: ['CustomMutationError', mutationKey],
        queryFn: async () => Promise.resolve(false as unknown as TError),
        retry: false,
        gcTime: Infinity,
        staleTime: Infinity,
    });
    const mutation = useMutation<TData, TError, TVariables, TContext>({
        mutationKey,
        mutationFn: async (...params) => {
            queryClient.setQueryData(['CustomMutationError', mutationKey], false);
            return mutationFn?.(...params);
        },

        ...options,
        onSuccess: (data, variables, context) => {
            queryClient.setQueryData(['CustomMutation', mutationKey], data);
            if (options?.onSuccess) options.onSuccess(data, variables, context);
        },
        onError: (err, variables, context) => {
            queryClient.setQueryData(['CustomMutationError', mutationKey], err);
            if (options?.onError) options.onError(err, variables, context);
        },
    });
    const isLoading = useIsMutating({ mutationKey });

    const reset = () => {
        queryClient.setQueryData(['CustomMutation', mutationKey], null);
        queryClient.setQueryData(['CustomMutationError', mutationKey], null);
        mutation.reset();
    };

    // We need typecasting here due the ADT about the mutation result, and as we're using a data not related to the mutation result
    // The typescript can't infer the type correctly.
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    return {
        ...mutation,
        data: query.data,
        isPending: !!isLoading,
        error: queryError.data,
        isError: !!queryError.data,
        reset,
    } as UseMutationResult<TData, TError, TVariables, TContext>;
};
